import { useBranchStore } from '@/app/store/use-branch-store';
import { ICartItem } from '@/entities/cart/model/types';
import { useCartStore } from '@/entities/cart/model/use-cart-store';
import { useDeliveryTypeStore } from '@/features/select-delivery-type/model/use-delivery-type-store';
import { useCustomLocale } from '@/shared/hooks/use-custom-locale';
import { useCallback, useEffect, useMemo } from 'react';
import { useAutoAddProducts } from '../api/query';
import { IAutoAddingProduct } from './types';

type Props = {
  enabled?: boolean;
};

export default function useAutoAddingProduct({ enabled = true }: Props) {
  const currentLocale = useCustomLocale();
  const { cart } = useCartStore();
  const { ADD_TO_CART, REMOVE } = useCartStore();
  const { deliveryType } = useDeliveryTypeStore();
  const { branch } = useBranchStore();

  const cartItems = useMemo(
    () =>
      cart
        ?.filter((item) => !item?.default_product)
        .reduce<Record<string, number>>((acc, { product_id, quantity }) => {
          acc[product_id] = quantity;
          return acc;
        }, {}),
    [cart]
  );

  const { data: autoAddProductData, isLoading } = useAutoAddProducts(
    {
      page: 1,
      limit: 10,
      order_type_id: deliveryType?.id,
      is_active: true,
      items: JSON.stringify(cartItems),
      menu_id: branch?.menu_id || '',
      source_id: cart?.[0]?.source_id,
      partner_shipper_id: cart?.[0]?.shipper_id,
    },
    {
      enabled: enabled && Boolean(cart?.length && cart?.[0]?.shipper_id),
    }
  );

  const upsertCartHandler = useCallback((product: IAutoAddingProduct) => {
    ADD_TO_CART({
      key: '',
      comment: '',
      in_stop: false,
      is_optional: !!product?.is_optional,
      price: product?.product?.out_price || 0,
      price_with_discount: product?.product?.out_price || 0,
      product_id: product?.product_id,
      type: product?.product?.type,
      variants: {},
      order_modifiers: [],
      quantity: product?.min_qty,
      min_qty: product?.min_qty,
      max_qty: product?.max_qty,
      default_product: true,
      category_name: product?.product?.slug,
      product_name: product?.product?.title?.[currentLocale],
      shipper_id: cart[0]?.shipper_id,
      branch_id: branch?.id,
      source_id: cart[0]?.source_id,
      menu_id: branch?.menu_id || '',
      brand_id: cart[0]?.brand_id,
    });
  }, []);

  useEffect(() => {
    const manipulateProducts = () => {
      if (isLoading) return;

      if (autoAddProductData && autoAddProductData.length) {
        autoAddProductData?.forEach((element) => {
          const current = cart.find(
            (item) => item.product_id === element.product_id
          );
          if (!current) {
            upsertCartHandler(element);
          } else if (isUpdateNeeded(current, element)) {
            REMOVE(current.key);
            upsertCartHandler(element);
          }
        });
      }

      cart.forEach((item) => {
        if (!item?.default_product) return;
        const stillValid = autoAddProductData?.some(
          (p) => p.product_id === item.product_id
        );

        if (!stillValid) {
          REMOVE(item.key);
        }
      });
    };

    manipulateProducts();
  }, [upsertCartHandler, autoAddProductData]);

  const productIds = useMemo(() => {
    return String([...cart?.map((product) => product.product_id)]);
  }, [cart]);

  return { productIds };
}

const isUpdateNeeded = (item: ICartItem, autoAddProduct: IAutoAddingProduct) =>
  item?.min_qty !== autoAddProduct?.min_qty ||
  item?.max_qty !== autoAddProduct?.max_qty ||
  item?.is_optional !== (autoAddProduct?.is_optional || false);
